<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-lungs me-2"></i>{{ $t('page.tb_register', 'TB Register (TIBU)') }}</h4>
        </div>
        <button v-if="canAccess" class="hms-btn hms-btn-blue" @click="loadAll"><i class="mdi mdi-refresh"></i></button>
      </div>

      <div v-if="!canAccess" class="hms-card text-center" style="padding:40px; background:linear-gradient(135deg,#fef3c7,#fde68a); border:1px dashed #d97706;">
        <i class="mdi mdi-shield-alert-outline" style="font-size:48px; color:#b45309;"></i>
        <h4 class="mt-2" style="color:#78350f;">Clinical Access Only</h4>
        <p style="color:#92400e;">The TB register contains protected information about people living with TB. Access is restricted to clinical roles (including pharmacy for DOTS dispensing). This access attempt has been audit-logged.</p>
        <button class="hms-btn hms-btn-neutral" @click="$router.push('/dashboard')">Back to Dashboard</button>
      </div>
      <div v-else>

      <!-- STATS -->
      <div class="hms-stat-row">
        <div class="hms-stat"><div class="hms-stat-num hms-stat-info">{{ stats.on_treatment || 0 }}</div><div class="hms-stat-lbl">On Treatment</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-success">{{ stats.new_quarter || 0 }}</div><div class="hms-stat-lbl">New (90d)</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ stats.hiv_coinfected || 0 }}</div><div class="hms-stat-lbl">HIV co-infected</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-danger">{{ stats.drug_resistant || 0 }}</div><div class="hms-stat-lbl">DR-TB</div></div>
        <div class="hms-stat"><div class="hms-stat-num hms-stat-warning">{{ defaulterStats.lost || 0 }}</div><div class="hms-stat-lbl">Lost (&gt;60d)</div></div>
      </div>

      <!-- TABS -->
      <div class="hms-tab-row">
        <button class="hms-tab" :class="{ active: tab==='clients' }"   @click="tab='clients'"><i class="mdi mdi-account-group me-1"></i>Active Clients</button>
        <button class="hms-tab" :class="{ active: tab==='detail' }"    @click="tab='detail'" :disabled="!detail.client"><i class="mdi mdi-card-account-details me-1"></i>Client Card</button>
        <button class="hms-tab" :class="{ active: tab==='defaulters' }" @click="tab='defaulters'; loadDefaulters()"><i class="mdi mdi-alert me-1"></i>Defaulters</button>
        <button class="hms-tab" :class="{ active: tab==='cohort' }"    @click="tab='cohort'; loadCohort()"><i class="mdi mdi-chart-pie me-1"></i>Cohort Outcomes</button>
        <button class="hms-tab" :class="{ active: tab==='indicators' }" @click="tab='indicators'; loadIndicators()"><i class="mdi mdi-chart-box me-1"></i>MoH 711</button>
      </div>

      <!-- ============ CLIENTS ============ -->
      <div v-if="tab==='clients'">
        <div class="d-flex justify-content-between mb-2">
          <div class="d-flex gap-2">
            <input v-model="clientSearch" class="hms-input" placeholder="Name / TB # / DTB # / phone" @keyup.enter="loadClients" style="min-width:280px" />
            <select v-model="statusFilter" class="hms-input" @change="loadClients">
              <option value="on_treatment">On treatment</option>
              <option value="cured">Cured</option>
              <option value="completed">Completed</option>
              <option value="treatment_failed">Failed</option>
              <option value="died">Died</option>
              <option value="lost_to_followup">Lost</option>
              <option value="transferred_out">Transferred out</option>
            </select>
            <button class="hms-btn hms-btn-blue" @click="loadClients">Search</button>
          </div>
          <button class="hms-btn hms-btn-success" @click="openEnrolModal()">
            <i class="mdi mdi-account-plus"></i> Enrol
          </button>
        </div>
        <table class="hms-table">
          <thead>
            <tr>
              <th>TB #</th><th>Name</th><th>Type</th><th>Class.</th><th>Cat.</th>
              <th>Regimen</th><th>Mo</th><th>HIV</th><th>Next appt</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="c in clients" :key="c.tb_id" :class="rowClass(c)">
              <td><strong>{{ c.tb_number }}</strong><div class="hms-muted-sm">{{ c.district_tb_number || '' }}</div></td>
              <td>{{ c.patient_name }}<div class="hms-muted-sm">{{ c.patient_no }} · {{ ageString(c.date_of_birth) }} {{ c.sex || '' }}</div></td>
              <td>{{ formatPatientType(c.patient_type) }}</td>
              <td><span class="hms-pill hms-pill-info">{{ formatClassification(c.disease_classification) }}</span></td>
              <td><span class="hms-pill" :class="catPill(c.treatment_category)">{{ formatCategory(c.treatment_category) }}</span></td>
              <td>{{ c.regimen || '—' }}</td>
              <td>{{ c.months_on_tx != null ? c.months_on_tx : 0 }}</td>
              <td>
                <span v-if="c.hiv_status === 'positive'" class="hms-pill hms-pill-warn">HIV+</span>
                <span v-else-if="c.hiv_status === 'negative'" class="hms-pill hms-pill-success">HIV-</span>
                <span v-else class="hms-pill hms-pill-muted">{{ c.hiv_status }}</span>
                <div v-if="c.art_number" class="hms-muted-sm">{{ c.art_number }}</div>
              </td>
              <td :class="c.days_to_appointment != null && c.days_to_appointment < 0 ? 'st-bad' : ''">
                {{ c.next_appointment_date || '—' }}
                <div v-if="c.days_to_appointment != null" class="hms-muted-sm">
                  {{ c.days_to_appointment >= 0 ? 'in ' + c.days_to_appointment + 'd' : Math.abs(c.days_to_appointment) + 'd late' }}
                </div>
              </td>
              <td><button class="hms-btn hms-btn-blue hms-btn-sm" @click="openDetail(c)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ DETAIL ============ -->
      <div v-if="tab==='detail' && detail.client">
        <div class="hms-card mb-3">
          <div class="hms-card-body">
            <div class="d-flex justify-content-between flex-wrap gap-3">
              <div>
                <div class="hms-name-big">{{ detail.client.patient_name }}</div>
                <div class="hms-muted-sm">{{ detail.client.tb_number }} · DTB: {{ detail.client.district_tb_number || '—' }}</div>
                <div class="hms-muted-sm">{{ detail.client.patient_no }} · {{ ageString(detail.client.date_of_birth) }} {{ detail.client.sex || '' }} · {{ detail.client.phone || '' }}</div>
                <div class="hms-muted-sm">DOT: {{ detail.client.dot_supporter_name || '—' }} <span v-if="detail.client.dot_supporter_phone">({{ detail.client.dot_supporter_phone }})</span></div>
              </div>
              <div class="text-end">
                <div><strong>{{ detail.client.regimen }}</strong> <span class="hms-pill" :class="catPill(detail.client.treatment_category)">{{ formatCategory(detail.client.treatment_category) }}</span></div>
                <div class="hms-muted-sm">Started {{ detail.client.treatment_started_date || '—' }}</div>
                <div class="hms-muted-sm">Expected end: {{ detail.client.expected_completion_date || '—' }}</div>
                <div class="hms-muted-sm">Next appt: <strong>{{ detail.client.next_appointment_date || '—' }}</strong></div>
                <div v-if="detail.client.art_number" class="hms-muted-sm"><i class="mdi mdi-ribbon"></i> ART: {{ detail.client.art_number }}</div>
              </div>
            </div>
            <div class="d-flex gap-2 mt-3 flex-wrap">
              <button class="hms-btn hms-btn-success hms-btn-sm" @click="openVisitModal()"><i class="mdi mdi-clipboard-text"></i> Record Visit / Refill</button>
              <button class="hms-btn hms-btn-blue hms-btn-sm" @click="openContactModal()"><i class="mdi mdi-account-multiple-plus"></i> Add Contact</button>
              <button class="hms-btn hms-btn-amber hms-btn-sm" @click="openOutcomeModal()"><i class="mdi mdi-flag-checkered"></i> Set Outcome</button>
            </div>
          </div>
        </div>

        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">Sputum follow-up</div>
            <div class="hms-card-body">
              <table v-if="sputumRows.length" class="hms-table">
                <thead><tr><th>Visit date</th><th>Mo</th><th>Smear</th><th>GeneXpert</th><th>Culture</th></tr></thead>
                <tbody>
                  <tr v-for="v in sputumRows" :key="v.visit_id" :class="v.smear_result === 'positive' ? 'row-warn' : ''">
                    <td>{{ v.visit_date }}</td>
                    <td>{{ v.treatment_month != null ? 'M' + v.treatment_month : '—' }}</td>
                    <td>{{ formatSmear(v.smear_result, v.smear_grade) }}</td>
                    <td>{{ formatGenexpert(v.genexpert_result) }}</td>
                    <td>{{ formatCulture(v.culture_result) }}</td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-muted text-center hms-pad">No sputum follow-ups yet.</div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Contact tracing ({{ detail.contacts.length }})</div>
            <div class="hms-card-body">
              <table v-if="detail.contacts.length" class="hms-table">
                <thead><tr><th>Name</th><th>Rel.</th><th>Age</th><th>Screened</th><th>Outcome</th></tr></thead>
                <tbody>
                  <tr v-for="c in detail.contacts" :key="c.contact_id">
                    <td>{{ c.name }}<div class="hms-muted-sm">{{ c.phone || '' }}</div></td>
                    <td>{{ c.relationship || '—' }}</td>
                    <td>{{ c.age || '—' }} {{ c.sex || '' }}</td>
                    <td>
                      <span v-if="c.screened == 1" class="hms-pill hms-pill-success">Yes</span>
                      <span v-else class="hms-pill hms-pill-warn">No</span>
                    </td>
                    <td><span class="hms-pill" :class="contactOutcomePill(c.outcome)">{{ formatContactOutcome(c.outcome) }}</span></td>
                  </tr>
                </tbody>
              </table>
              <div v-else class="hms-muted text-center hms-pad">No contacts traced yet.</div>
            </div>
          </div>
        </div>

        <div class="hms-card mt-3">
          <div class="hms-card-head">Visit History</div>
          <div class="hms-card-body p-0">
            <table class="hms-table">
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Mo</th><th>Wt</th><th>DOT</th>
                  <th>Adherence</th><th>Drugs</th><th>Days</th><th>Next appt</th><th>By</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="v in detail.visits" :key="v.visit_id">
                  <td>{{ v.visit_date }}</td>
                  <td><span class="hms-pill hms-pill-muted">{{ formatVisitType(v.visit_type) }}</span></td>
                  <td>{{ v.treatment_month != null ? 'M' + v.treatment_month : '—' }}</td>
                  <td>{{ v.weight_kg || '—' }}</td>
                  <td><span v-if="v.dot_observed == 1"><i class="mdi mdi-check"></i></span></td>
                  <td><span class="hms-pill" :class="adherencePill(v.adherence)">{{ v.adherence || '—' }}</span></td>
                  <td>{{ v.drugs_dispensed || '—' }}</td>
                  <td>{{ v.days_dispensed || '—' }}</td>
                  <td>{{ v.next_appointment || '—' }}</td>
                  <td>{{ v.recorded_by_name || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ============ DEFAULTERS ============ -->
      <div v-if="tab==='defaulters'">
        <div v-if="!defaulters.length" class="hms-empty">
          <i class="mdi mdi-check-circle"></i><h5>No defaulters</h5>
        </div>
        <table v-else class="hms-table">
          <thead>
            <tr>
              <th>TB #</th><th>Name</th><th>Regimen</th><th>Cat.</th><th>Last appt</th>
              <th>Days late</th><th>Phone</th><th>DOT supporter</th><th>CHV</th><th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="d in defaulters" :key="d.tb_id" :class="d.days_late > 60 ? 'row-action' : (d.days_late >= 14 ? 'row-warn' : '')">
              <td><strong>{{ d.tb_number }}</strong></td>
              <td>{{ d.patient_name }}</td>
              <td>{{ d.regimen }}</td>
              <td><span class="hms-pill" :class="catPill(d.treatment_category)">{{ formatCategory(d.treatment_category) }}</span></td>
              <td>{{ d.next_appointment_date }}</td>
              <td><strong :class="d.days_late > 14 ? 'st-bad' : ''">{{ d.days_late }}d</strong></td>
              <td>{{ d.phone || '—' }}</td>
              <td>{{ d.dot_supporter_name || '—' }} <span class="hms-muted-sm">{{ d.dot_supporter_phone }}</span></td>
              <td>{{ d.chv_name || '—' }} <span class="hms-muted-sm">{{ d.chv_phone }}</span></td>
              <td><button class="hms-btn-link" @click="openDetailById(d.tb_id)"><i class="mdi mdi-folder-open"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ============ COHORT ============ -->
      <div v-if="tab==='cohort'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="cohortPeriod" class="hms-input" @change="loadCohort" style="max-width:240px">
            <option value="last_quarter">Last quarter cohort</option>
            <option value="last_year">12-month cohort (eval at 6m)</option>
            <option value="this_year">This year (interim)</option>
          </select>
        </div>
        <div class="hms-grid-2">
          <div class="hms-card">
            <div class="hms-card-head">Cohort outcomes — {{ cohortPeriodLabel }}</div>
            <div class="hms-card-body">
              <div class="hms-ind-row"><span>Cohort size</span><strong>{{ cohort.cohort_size || 0 }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-check-circle text-success"></i> Cured</span><strong>{{ cohort.cured || 0 }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-check text-success"></i> Completed</span><strong>{{ cohort.completed || 0 }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-close-circle text-danger"></i> Treatment failed</span><strong>{{ cohort.treatment_failed || 0 }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-grave-stone"></i> Died</span><strong>{{ cohort.died || 0 }}</strong></div>
              <div class="hms-ind-row"><span><i class="mdi mdi-account-off"></i> Lost to follow-up</span><strong>{{ cohort.lost_to_followup || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Transferred out</span><strong>{{ cohort.transferred_out || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Not evaluated</span><strong>{{ cohort.not_evaluated || 0 }}</strong></div>
              <div class="hms-ind-row"><span>Still on treatment</span><strong>{{ cohort.still_on_tx || 0 }}</strong></div>
            </div>
          </div>
          <div class="hms-card">
            <div class="hms-card-head">Treatment Success Rate</div>
            <div class="hms-card-body">
              <div class="hms-suppression">
                <div class="hms-supp-label">Cured + Completed / Cohort</div>
                <div class="hms-supp-bar"><div class="hms-supp-fill" :style="{ width: tsr + '%' }"></div></div>
                <div class="hms-supp-num">{{ tsr }}%</div>
                <div class="hms-muted-sm text-center mt-2">WHO target ≥ 90%</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- ============ INDICATORS / 711 ============ -->
      <div v-if="tab==='indicators'">
        <div class="d-flex gap-2 mb-3">
          <select v-model="indicatorPeriod" class="hms-input" @change="loadIndicators" style="max-width:200px">
            <option value="this_month">This month</option>
            <option value="this_quarter">This quarter</option>
            <option value="this_year">This year</option>
          </select>
        </div>
        <div class="hms-card">
          <div class="hms-card-head">MoH 711 — TB notification &amp; care</div>
          <div class="hms-card-body">
            <div class="hms-grid-2">
              <div>
                <div class="hms-ind-row"><span>Currently on treatment</span><strong>{{ indicators.currently_on_tx || 0 }}</strong></div>
                <div class="hms-ind-row"><span>Notified (period)</span><strong>{{ indicators.notified_period || 0 }}</strong></div>
                <div class="hms-ind-row"><span>New cases</span><strong>{{ indicators.new_period || 0 }}</strong></div>
                <div class="hms-ind-row"><span>Re-treatment</span><strong>{{ indicators.retreatment_period || 0 }}</strong></div>
                <div class="hms-ind-row"><span>Bacteriologically confirmed</span><strong>{{ indicators.bacteriologically_confirmed || 0 }}</strong></div>
              </div>
              <div>
                <div class="hms-ind-row"><span>EPTB</span><strong>{{ indicators.eptb || 0 }}</strong></div>
                <div class="hms-ind-row"><span>HIV co-infected</span><strong>{{ indicators.hiv_coinfected || 0 }}</strong></div>
                <div class="hms-ind-row"><span>Drug-resistant TB</span><strong>{{ indicators.drug_resistant || 0 }}</strong></div>
                <div class="hms-ind-row"><span>Contacts traced</span><strong>{{ indicators.contacts_total || 0 }}</strong></div>
                <div class="hms-ind-row"><span>Contacts screened</span><strong>{{ indicators.contacts_screened || 0 }}</strong></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- =============== ENROL MODAL =============== -->
    <div v-if="enrolModal" class="hms-modal-bg" @click.self="enrolModal=false">
      <div class="hms-modal" style="width:760px">
        <div class="hms-modal-head"><h5>Enrol on TB Treatment</h5><button class="hms-icon-btn" @click="enrolModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div v-if="!enrol.patient_id">
            <label class="hms-label">Patient *</label>
            <div class="d-flex gap-2">
              <input v-model="enrol.patient_search" class="hms-input" placeholder="Name / patient # / phone" @keyup.enter="findEnrolPatient" />
              <button class="hms-btn hms-btn-blue" @click="findEnrolPatient">Find</button>
            </div>
          </div>
          <div v-else class="hms-patient-chip">
            <i class="mdi mdi-account"></i> {{ enrol.patient_name }} <a href="javascript:void(0)" class="ms-2" @click="enrol.patient_id=null">change</a>
          </div>
          <div class="row g-2 mt-2">
            <div class="col-4"><label class="hms-label">Enrolment date *</label><input v-model="enrol.enrollment_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">DTB # (district)</label><input v-model="enrol.district_tb_number" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Patient type</label>
              <select v-model="enrol.patient_type" class="hms-input">
                <option value="new">New</option>
                <option value="relapse">Relapse</option>
                <option value="tx_after_failure">Tx after failure</option>
                <option value="tx_after_loss">Tx after loss</option>
                <option value="transfer_in">Transfer in</option>
                <option value="other_previously_treated">Other prev. treated</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Disease classification</label>
              <select v-model="enrol.disease_classification" class="hms-input">
                <option value="PTB_bacteriologically_confirmed">PTB — bacteriologically confirmed</option>
                <option value="PTB_clinically_diagnosed">PTB — clinically diagnosed</option>
                <option value="EPTB">EPTB</option>
              </select>
            </div>
            <div class="col-4" v-if="enrol.disease_classification === 'EPTB'"><label class="hms-label">EPTB site</label><input v-model="enrol.eptb_site" class="hms-input" placeholder="lymph node / meningeal / pleural…" /></div>
            <div class="col-4"><label class="hms-label">Treatment category</label>
              <select v-model="enrol.treatment_category" class="hms-input">
                <option value="drug_sensitive">Drug-sensitive</option>
                <option value="isoniazid_resistant">Isoniazid-resistant (Hr)</option>
                <option value="RR_TB">Rifampicin-resistant (RR)</option>
                <option value="MDR_TB">MDR-TB</option>
                <option value="pre_XDR_TB">Pre-XDR</option>
                <option value="XDR_TB">XDR-TB</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Smear baseline</label>
              <select v-model="enrol.smear_baseline" class="hms-input">
                <option value="not_done">Not done</option><option value="negative">Negative</option>
                <option value="scanty">Scanty</option><option value="positive">Positive</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Grade</label>
              <select v-model="enrol.smear_baseline_grade" class="hms-input">
                <option value="">—</option><option value="1+">1+</option><option value="2+">2+</option><option value="3+">3+</option><option value="scanty">scanty</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">GeneXpert</label>
              <select v-model="enrol.genexpert_baseline" class="hms-input">
                <option value="not_done">Not done</option>
                <option value="mtb_detected_rif_sensitive">MTB+ Rif-sens</option>
                <option value="mtb_detected_rif_resistant">MTB+ Rif-resistant</option>
                <option value="mtb_detected_rif_indet">MTB+ Rif-indet</option>
                <option value="mtb_not_detected">MTB not detected</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Culture</label>
              <select v-model="enrol.culture_baseline" class="hms-input">
                <option value="not_done">Not done</option><option value="negative">Negative</option>
                <option value="positive">Positive</option><option value="contaminated">Contaminated</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">CXR finding</label><input v-model="enrol.cxr_finding" class="hms-input" placeholder="e.g. cavitation upper lobes, miliary…" /></div>
            <div class="col-3"><label class="hms-label">HIV status</label>
              <select v-model="enrol.hiv_status" class="hms-input">
                <option value="unknown">Unknown</option><option value="negative">Negative</option>
                <option value="positive">Positive</option><option value="declined">Declined</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">HIV test date</label><input v-model="enrol.hiv_test_date" type="date" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="enrol.cpt_started" :true-value="1" :false-value="0" /> CPT started</label></div>
            <div class="col-3"><label class="hms-label">DM status</label>
              <select v-model="enrol.dm_status" class="hms-input">
                <option value="not_screened">Not screened</option><option value="negative">Negative</option><option value="positive">Positive</option><option value="unknown">Unknown</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Regimen</label>
              <select v-model="enrol.regimen" class="hms-input">
                <option value="2RHZE/4RH">2RHZE / 4RH (new — drug-sensitive)</option>
                <option value="2RHZES/1RHZE/5RHE">2RHZES / 1RHZE / 5RHE (retreatment)</option>
                <option value="6Hr">6 Hr (Hr-resistant short)</option>
                <option value="MDR-shorter">All-oral shorter (MDR-TB)</option>
                <option value="MDR-longer">All-oral longer (MDR-TB)</option>
                <option value="paeds-2RHZ/4RH">2RHZ / 4RH (paediatric)</option>
              </select>
            </div>
            <div class="col-4"><label class="hms-label">Tx started</label><input v-model="enrol.treatment_started_date" type="date" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Expected completion</label><input v-model="enrol.expected_completion_date" type="date" class="hms-input" /></div>
            <div class="col-12"><div class="hms-section-label">DOT</div></div>
            <div class="col-4"><label class="hms-label">DOT supporter name</label><input v-model="enrol.dot_supporter_name" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Phone</label><input v-model="enrol.dot_supporter_phone" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Relationship</label><input v-model="enrol.dot_relationship" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">CHV name</label><input v-model="enrol.chv_name" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">CHV phone</label><input v-model="enrol.chv_phone" class="hms-input" /></div>
            <div class="col-4"><label class="hms-label">Next appointment</label><input v-model="enrol.next_appointment_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="enrol.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="enrolModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !enrol.patient_id" @click="saveEnrol">{{ saving?'Saving…':'Enrol' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== VISIT MODAL =============== -->
    <div v-if="visitModal" class="hms-modal-bg" @click.self="visitModal=false">
      <div class="hms-modal" style="width:680px">
        <div class="hms-modal-head"><h5>TB Visit / Refill</h5><button class="hms-icon-btn" @click="visitModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-3"><label class="hms-label">Date *</label><input v-model="visit.visit_date" type="date" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Type</label>
              <select v-model="visit.visit_type" class="hms-input">
                <option value="initiation">Initiation</option><option value="refill">Refill</option>
                <option value="monthly_review">Monthly review</option>
                <option value="sputum_followup">Sputum follow-up</option>
                <option value="outcome">Outcome visit</option>
              </select>
            </div>
            <div class="col-2"><label class="hms-label">Mo</label><input v-model.number="visit.treatment_month" type="number" min="0" max="24" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Wt (kg)</label><input v-model.number="visit.weight_kg" type="number" step="0.1" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Adherence</label>
              <select v-model="visit.adherence" class="hms-input">
                <option value="good">Good</option><option value="fair">Fair</option><option value="poor">Poor</option>
              </select>
            </div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="visit.dot_observed" :true-value="1" :false-value="0" /> DOT observed</label></div>
            <div class="col-3"><label class="hms-label">Smear</label>
              <select v-model="visit.smear_result" class="hms-input">
                <option value="not_done">Not done</option><option value="negative">Negative</option>
                <option value="scanty">Scanty</option><option value="positive">Positive</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">Smear grade</label>
              <select v-model="visit.smear_grade" class="hms-input">
                <option value="">—</option><option value="1+">1+</option><option value="2+">2+</option><option value="3+">3+</option><option value="scanty">scanty</option>
              </select>
            </div>
            <div class="col-3"><label class="hms-label">GeneXpert</label>
              <select v-model="visit.genexpert_result" class="hms-input">
                <option value="not_done">Not done</option>
                <option value="mtb_detected_rif_sensitive">MTB+ Rif-sens</option>
                <option value="mtb_detected_rif_resistant">MTB+ Rif-resistant</option>
                <option value="mtb_not_detected">MTB not detected</option>
                <option value="error">Error</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Drugs dispensed</label><input v-model="visit.drugs_dispensed" class="hms-input" placeholder="e.g. RHZE x28, RH x84" /></div>
            <div class="col-3"><label class="hms-label">Days dispensed</label><input v-model.number="visit.days_dispensed" type="number" class="hms-input" /></div>
            <div class="col-3"><label class="hms-label">Next appointment</label><input v-model="visit.next_appointment" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Side effects</label><textarea v-model="visit.side_effects" class="hms-input" rows="2"></textarea></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="visit.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="visitModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveVisit">{{ saving?'Saving…':'Save Visit' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== CONTACT MODAL =============== -->
    <div v-if="contactModal" class="hms-modal-bg" @click.self="contactModal=false">
      <div class="hms-modal" style="width:560px">
        <div class="hms-modal-head"><h5>Add / Edit Contact</h5><button class="hms-icon-btn" @click="contactModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-8"><label class="hms-label">Name *</label><input v-model="contact.name" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Age</label><input v-model.number="contact.age" type="number" class="hms-input" /></div>
            <div class="col-2"><label class="hms-label">Sex</label>
              <select v-model="contact.sex" class="hms-input"><option value="">—</option><option value="M">M</option><option value="F">F</option></select>
            </div>
            <div class="col-6"><label class="hms-label">Relationship</label><input v-model="contact.relationship" class="hms-input" placeholder="spouse / child / sibling…" /></div>
            <div class="col-6"><label class="hms-label">Phone</label><input v-model="contact.phone" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Address</label><input v-model="contact.address" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="contact.screened" :true-value="1" :false-value="0" /> Screened</label></div>
            <div class="col-3"><label class="hms-label">Screened on</label><input v-model="contact.screened_at" type="date" class="hms-input" /></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="contact.symptomatic" :true-value="1" :false-value="0" /> Symptomatic</label></div>
            <div class="col-3 d-flex align-items-end"><label><input type="checkbox" v-model="contact.tested" :true-value="1" :false-value="0" /> Tested</label></div>
            <div class="col-12"><label class="hms-label">Outcome</label>
              <select v-model="contact.outcome" class="hms-input">
                <option value="not_traced">Not traced</option>
                <option value="well">Well</option>
                <option value="on_ipt">On IPT/TPT</option>
                <option value="on_treatment">On TB treatment</option>
                <option value="presumptive">Presumptive — under investigation</option>
                <option value="refused">Refused</option>
              </select>
            </div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="contact.notes" class="hms-input" rows="2"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="contactModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving || !contact.name" @click="saveContact">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    <!-- =============== OUTCOME MODAL =============== -->
    <div v-if="outcomeModal" class="hms-modal-bg" @click.self="outcomeModal=false">
      <div class="hms-modal" style="width:520px">
        <div class="hms-modal-head"><h5>Set Treatment Outcome</h5><button class="hms-icon-btn" @click="outcomeModal=false"><i class="mdi mdi-close"></i></button></div>
        <div class="hms-modal-body">
          <div class="row g-2">
            <div class="col-6"><label class="hms-label">Outcome *</label>
              <select v-model="outcome.current_status" class="hms-input">
                <option value="cured">Cured (smear-/culture-confirmed)</option>
                <option value="completed">Treatment completed</option>
                <option value="treatment_failed">Treatment failed</option>
                <option value="died">Died</option>
                <option value="lost_to_followup">Lost to follow-up</option>
                <option value="not_evaluated">Not evaluated</option>
                <option value="transferred_out">Transferred out</option>
                <option value="stopped">Treatment stopped</option>
              </select>
            </div>
            <div class="col-6"><label class="hms-label">Outcome date</label><input v-model="outcome.outcome_date" type="date" class="hms-input" /></div>
            <div class="col-12"><label class="hms-label">Notes</label><textarea v-model="outcome.outcome_notes" class="hms-input" rows="3"></textarea></div>
          </div>
        </div>
        <div class="hms-modal-foot">
          <button class="hms-btn hms-btn-secondary" @click="outcomeModal=false">Cancel</button>
          <button class="hms-btn hms-btn-success" :disabled="saving" @click="saveOutcome">{{ saving?'Saving…':'Save' }}</button>
        </div>
      </div>
    </div>

    </div> <!-- /canAccess v-else -->

  </div>
</template>

<script>
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "TbRegister",
  components: { Navigation },
  data() {
    return {
      tab: 'clients', saving: false,
      canAccess: false,
      stats: {}, defaulterStats: {},

      clientSearch: '', statusFilter: 'on_treatment',
      clients: [],

      detail: { client: null, visits: [], contacts: [] },

      defaulters: [],

      cohortPeriod: 'last_year',
      cohort: {}, tsr: 0,

      indicatorPeriod: 'this_quarter',
      indicators: {},

      enrolModal: false, enrol: this.blankEnrol(),
      visitModal: false, visit: this.blankVisit(),
      contactModal: false, contact: this.blankContact(),
      outcomeModal: false, outcome: { current_status: 'cured', outcome_date: '', outcome_notes: '' }
    };
  },
  computed: {
    sputumRows() {
      return (this.detail.visits || []).filter(v =>
        v.smear_result !== 'not_done' || v.genexpert_result !== 'not_done' || v.culture_result !== 'not_done'
      );
    },
    cohortPeriodLabel() {
      return ({ last_quarter: 'Last quarter', last_year: '12-month cohort', this_year: 'This year (interim)' })[this.cohortPeriod] || '';
    }
  },
  async mounted() {
    try {
      var r = await executeJson({ function: 'canAccessSensitiveRegister', module: 'tb' }, hms);
      this.canAccess = !!(r.data && r.data.success && r.data.data && r.data.data.can);
    } catch (e) { this.canAccess = false; }
    if (this.canAccess) this.loadAll();
  },
  methods: {
    blankEnrol() {
      return {
        patient_id: null, patient_name: '', patient_search: '',
        district_tb_number: '', enrollment_date: new Date().toISOString().substring(0,10),
        patient_type: 'new', disease_classification: 'PTB_bacteriologically_confirmed', eptb_site: '',
        treatment_category: 'drug_sensitive',
        smear_baseline: 'not_done', smear_baseline_grade: '',
        genexpert_baseline: 'not_done', culture_baseline: 'not_done',
        cxr_finding: '',
        hiv_status: 'unknown', hiv_test_date: '', cpt_started: 0, dm_status: 'not_screened',
        regimen: '2RHZE/4RH',
        treatment_started_date: new Date().toISOString().substring(0,10),
        expected_completion_date: '',
        next_appointment_date: '',
        dot_supporter_name: '', dot_supporter_phone: '', dot_relationship: '',
        chv_name: '', chv_phone: '', notes: ''
      };
    },
    blankVisit() {
      return {
        visit_date: new Date().toISOString().substring(0,10), visit_type: 'refill',
        treatment_month: null, weight_kg: null, adherence: 'good', dot_observed: 0,
        smear_result: 'not_done', smear_grade: '', genexpert_result: 'not_done', culture_result: 'not_done',
        drugs_dispensed: '', days_dispensed: 28, side_effects: '', next_appointment: '', notes: ''
      };
    },
    blankContact() {
      return { contact_id: null, name: '', relationship: '', age: null, sex: '', phone: '', address: '',
               screened: 0, screened_at: '', symptomatic: 0, tested: 0, outcome: 'not_traced', notes: '' };
    },

    async loadAll() {
      await Promise.all([this.loadClients(), this.loadDefaulters()]);
    },
    async loadClients() {
      try {
        const r = await executeJson({ function: 'getTbClients', search: this.clientSearch, status: this.statusFilter }, hms);
        if (r.data && r.data.success) { const d = r.data.data || {}; this.clients = d.Clients || []; this.stats = d.Stats || {}; }
      } catch (e) { console.error(e); }
    },

    openEnrolModal() { this.enrol = this.blankEnrol(); this.enrolModal = true; },
    async findEnrolPatient() {
      const q = (this.enrol.patient_search || '').trim();
      if (!q) return;
      const r = await executeJson({ function: 'getPatients', search: q, limit: 1 }, hms);
      if (r.data && r.data.success) {
        const d = r.data.data || {};
        const p = (d.Patients || d.patients || [])[0];
        if (!p) { alert('No patient found.'); return; }
        this.enrol.patient_id = p.patient_id;
        this.enrol.patient_name = p.first_name + ' ' + p.last_name;
      }
    },
    async saveEnrol() {
      this.saving = true;
      try {
        const params = { function: 'enrolTbClient', ...this.enrol };
        delete params.patient_search; delete params.patient_name;
        const r = await executeJson(params, hms);
        if (r.data && r.data.success) { this.enrolModal = false; await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async openDetail(c) { await this.openDetailById(c.tb_id); },
    async openDetailById(tbId) {
      try {
        const r = await executeJson({ function: 'getTbClientDetail', tb_id: tbId }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.detail.client   = d.Client;
          this.detail.visits   = d.Visits   || [];
          this.detail.contacts = d.Contacts || [];
          this.tab = 'detail';
        }
      } catch (e) { console.error(e); }
    },

    openVisitModal() { this.visit = this.blankVisit(); this.visitModal = true; },
    async saveVisit() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'recordTbVisit', tb_id: this.detail.client.tb_id, ...this.visit }, hms);
        if (r.data && r.data.success) { this.visitModal = false; await this.openDetailById(this.detail.client.tb_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openContactModal(c) {
      this.contact = c ? { ...this.blankContact(), ...c } : this.blankContact();
      this.contactModal = true;
    },
    async saveContact() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'saveTbContact', tb_id: this.detail.client.tb_id, ...this.contact }, hms);
        if (r.data && r.data.success) { this.contactModal = false; await this.openDetailById(this.detail.client.tb_id); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    openOutcomeModal() {
      this.outcome = { current_status: 'cured', outcome_date: new Date().toISOString().substring(0,10), outcome_notes: '' };
      this.outcomeModal = true;
    },
    async saveOutcome() {
      if (!this.detail.client) return;
      this.saving = true;
      try {
        const r = await executeJson({ function: 'updateTbClient', tb_id: this.detail.client.tb_id, ...this.outcome }, hms);
        if (r.data && r.data.success) { this.outcomeModal = false; await this.openDetailById(this.detail.client.tb_id); await this.loadClients(); }
        else alert((r.data && r.data.message) || 'Failed.');
      } catch (e) { console.error(e); }
      finally { this.saving = false; }
    },

    async loadDefaulters() {
      try {
        const r = await executeJson({ function: 'getTbDefaulters' }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.defaulters = d.Defaulters || [];
          this.defaulterStats = d.Stats || {};
        }
      } catch (e) { console.error(e); }
    },
    async loadCohort() {
      try {
        const r = await executeJson({ function: 'getTbCohortOutcomes', period: this.cohortPeriod }, hms);
        if (r.data && r.data.success) {
          const d = r.data.data || {};
          this.cohort = d.Cohort || {};
          this.tsr = d.TreatmentSuccessPct || 0;
        }
      } catch (e) { console.error(e); }
    },
    async loadIndicators() {
      try {
        const r = await executeJson({ function: 'getTbIndicators', period: this.indicatorPeriod }, hms);
        if (r.data && r.data.success) {
          this.indicators = (r.data.data && r.data.data.Indicators) || {};
        }
      } catch (e) { console.error(e); }
    },

    rowClass(c) {
      if (c.days_to_appointment != null && c.days_to_appointment < -7) return 'row-warn';
      if (['MDR_TB','RR_TB','XDR_TB','pre_XDR_TB'].includes(c.treatment_category)) return 'row-action-mild';
      return '';
    },
    catPill(c) {
      return ({ drug_sensitive: 'hms-pill-success', isoniazid_resistant: 'hms-pill-warn',
                RR_TB: 'hms-pill-danger', MDR_TB: 'hms-pill-danger',
                pre_XDR_TB: 'hms-pill-danger', XDR_TB: 'hms-pill-danger' })[c] || 'hms-pill-muted';
    },
    formatPatientType(t) {
      return ({ new: 'New', relapse: 'Relapse', tx_after_failure: 'Tx after failure',
                tx_after_loss: 'Tx after loss', transfer_in: 'Transfer in',
                other_previously_treated: 'Other prev. treated' })[t] || t;
    },
    formatClassification(c) {
      return ({ PTB_bacteriologically_confirmed: 'PTB bact-conf', PTB_clinically_diagnosed: 'PTB clin',
                EPTB: 'EPTB', presumptive: 'Presumptive', no_tb: 'No TB' })[c] || c;
    },
    formatCategory(c) {
      return ({ drug_sensitive: 'DS-TB', isoniazid_resistant: 'Hr-resist',
                RR_TB: 'RR-TB', MDR_TB: 'MDR-TB',
                pre_XDR_TB: 'Pre-XDR', XDR_TB: 'XDR-TB' })[c] || c;
    },
    formatVisitType(t) {
      return ({ initiation: 'Initiation', refill: 'Refill', monthly_review: 'Monthly review',
                sputum_followup: 'Sputum f/u', outcome: 'Outcome', transfer_in: 'Transfer in' })[t] || t;
    },
    formatSmear(r, g) {
      if (r === 'not_done') return '—';
      if (r === 'positive' && g) return 'POS ' + g;
      if (r === 'positive') return 'POS';
      if (r === 'scanty') return 'scanty';
      return r;
    },
    formatGenexpert(r) {
      return ({ mtb_detected_rif_sensitive: 'MTB+ Rif-sens',
                mtb_detected_rif_resistant: 'MTB+ Rif-RES',
                mtb_not_detected: 'MTB-',
                error: 'error', not_done: '—' })[r] || r;
    },
    formatCulture(r) { return ({ positive: 'POS', negative: 'NEG', contaminated: 'contam', not_done: '—' })[r] || r; },
    formatContactOutcome(o) {
      return ({ not_traced: 'Not traced', well: 'Well', on_ipt: 'On IPT/TPT',
                on_treatment: 'On TB tx', presumptive: 'Presumptive', refused: 'Refused' })[o] || o;
    },
    contactOutcomePill(o) {
      return ({ well: 'hms-pill-success', on_ipt: 'hms-pill-info', on_treatment: 'hms-pill-warn',
                presumptive: 'hms-pill-warn', refused: 'hms-pill-danger', not_traced: 'hms-pill-muted' })[o] || '';
    },
    adherencePill(a) { return ({ good: 'hms-pill-success', fair: 'hms-pill-warn', poor: 'hms-pill-danger' })[a] || ''; },
    ageString(dob) {
      if (!dob) return '';
      const d = new Date(dob.replace ? dob.replace(' ','T') : dob);
      const days = Math.floor((Date.now() - d.getTime()) / 86400000);
      if (days < 730) return Math.floor(days / 30) + 'mo';
      return Math.floor(days / 365) + 'y';
    }
  }
};
</script>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }

.hms-stat-row { display: grid; grid-template-columns: repeat(5, 1fr); gap: 12px; margin-bottom: 16px; }
.hms-stat { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; padding: 14px; text-align: center; }
.hms-stat-num { font-size: 28px; font-weight: 700; }
.hms-stat-lbl { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; margin-top: 2px; }
.hms-stat-success { color: #276749; } .hms-stat-info { color: #2c5282; }
.hms-stat-warning { color: #c05621; } .hms-stat-danger { color: #c53030; }

.hms-tab-row { display: flex; gap: 4px; border-bottom: 1px solid #e2e8f0; margin-bottom: 16px; }
.hms-tab { padding: 10px 16px; border: none; background: none; cursor: pointer; color: #718096; border-bottom: 2px solid transparent; font-size: 14px; }
.hms-tab.active { color: #2b6cb0; border-bottom-color: #2b6cb0; font-weight: 600; }
.hms-tab:disabled { opacity: .4; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 8px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #2d3748; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }
.hms-grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }

.hms-name-big { font-size: 20px; font-weight: 700; color: #1a202c; }

.hms-table { width: 100%; border-collapse: collapse; font-size: 13px; background: #fff; }
.hms-table th { background: #f7fafc; padding: 9px; border-bottom: 1px solid #e2e8f0; font-weight: 600; color: #4a5568; text-align: left; }
.hms-table td { padding: 8px 9px; border-bottom: 1px solid #edf2f7; vertical-align: middle; }
.hms-table tr.row-warn   { background: #fffaf0; }
.hms-table tr.row-action { background: #fff5f5; }
.hms-table tr.row-action-mild { background: #fefcbf33; }

.hms-input { width: 100%; padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-label { font-size: 11px; font-weight: 600; color: #4a5568; display: block; margin-bottom: 3px; }
.hms-section-label { font-size: 11px; font-weight: 700; color: #4a5568; letter-spacing: 1px; padding: 4px 0; border-bottom: 1px solid #edf2f7; margin: 6px 0 8px; }

.hms-pill { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 600; }
.hms-pill-success { background: #c6f6d5; color: #22543d; }
.hms-pill-info    { background: #bee3f8; color: #2a4365; }
.hms-pill-warn    { background: #feebc8; color: #7b341e; }
.hms-pill-danger  { background: #fed7d7; color: #742a2a; }
.hms-pill-muted   { background: #edf2f7; color: #4a5568; }

.hms-empty { background: #fff; border: 1px dashed #cbd5e0; border-radius: 8px; padding: 48px; text-align: center; color: #718096; }
.hms-empty .mdi { font-size: 48px; color: #38a169; }

.hms-modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center; z-index: 100; padding: 20px; }
.hms-modal { background: #fff; border-radius: 8px; max-height: 92vh; display: flex; flex-direction: column; }
.hms-modal-head { padding: 14px 18px; border-bottom: 1px solid #e2e8f0; display: flex; justify-content: space-between; align-items: center; }
.hms-modal-body { padding: 16px 18px; overflow-y: auto; }
.hms-modal-foot { padding: 12px 18px; border-top: 1px solid #e2e8f0; display: flex; justify-content: flex-end; gap: 8px; }
.hms-icon-btn { background: none; border: none; cursor: pointer; }
.hms-patient-chip { display: inline-flex; align-items: center; gap: 4px; background: #ebf8ff; color: #2c5282; padding: 6px 10px; border-radius: 6px; margin-top: 6px; font-size: 13px; }

.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 7px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-sm { padding: 4px 10px; font-size: 12px; }
.hms-btn-blue      { background: #2b6cb0; color: #fff; }
.hms-btn-success   { background: #38a169; color: #fff; }
.hms-btn-amber     { background: #d69e2e; color: #fff; }
.hms-btn-secondary { background: #fff; color: #2d3748; border: 1px solid #cbd5e0; }
.hms-btn-link      { background: transparent; color: #2b6cb0; border: none; cursor: pointer; }

.hms-ind-row { display: flex; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #edf2f7; font-size: 14px; }
.hms-ind-row strong { color: #2d3748; font-size: 16px; }

.hms-suppression { margin-top: 14px; }
.hms-supp-label  { font-size: 11px; color: #718096; text-transform: uppercase; letter-spacing: .5px; }
.hms-supp-bar    { background: #edf2f7; border-radius: 6px; height: 18px; overflow: hidden; margin: 4px 0; }
.hms-supp-fill   { background: linear-gradient(90deg, #38a169, #276749); height: 100%; }
.hms-supp-num    { text-align: right; font-weight: 700; color: #276749; font-size: 18px; }

.st-bad { color: #c53030; font-weight: 600; }
.text-success { color: #38a169; }
.text-danger  { color: #e53e3e; }
.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }
</style>
